import { cn } from './cn.js'

export interface MacroPreviewProps {
  /** Omit when the calorie figure is already displayed elsewhere. */
  kcal?: number | null
  proteinG: number
  carbsG: number
  fatG: number
  /** Second row of label nutrients; null renders an em dash. */
  extended?: {
    sugarsG: number | null
    satFatG: number | null
    fiberG: number | null
    saltG: number | null
  }
  /** 'card' draws its own hairline box; 'plain' embeds into an existing card. */
  frame?: 'card' | 'plain'
  className?: string
}

/**
 * The one macro strip. Every sheet used to hand-roll this grid with its own
 * column count and its own abbreviations; the diary rows already speak in
 * "B · A · R", so the strip does too, with the macro colors the Today bars use
 * carrying the meaning across screens.
 */
export function MacroPreview({
  kcal,
  proteinG,
  carbsG,
  fatG,
  extended,
  frame = 'card',
  className,
}: MacroPreviewProps) {
  const showKcal = kcal != null
  return (
    <dl
      className={cn(
        'grid gap-2',
        // The label nutrients are a second row of four, so they force the wider
        // grid even when there is no calorie cell to lead it.
        showKcal || extended != null ? 'grid-cols-4' : 'grid-cols-3',
        frame === 'card' &&
          'rounded-[var(--radius-card)] border border-line bg-surface px-3 py-2.5',
        className,
      )}
    >
      {showKcal && <Cell label="kcal" value={String(Math.round(kcal))} />}
      <Cell label="B" full="Baltymai" dot="var(--g-protein)" value={grams(proteinG)} />
      <Cell label="A" full="Angliavandeniai" dot="var(--g-carbs)" value={grams(carbsG)} />
      <Cell label="R" full="Riebalai" dot="var(--g-fat)" value={grams(fatG)} />
      {extended && (
        <>
          {/* Keeps the second row aligned under the macros in a 4-column grid. */}
          {!showKcal && <span aria-hidden="true" />}
          <Cell label="Cukr." full="Cukrus" value={grams(extended.sugarsG)} />
          <Cell label="Soč." full="Sočiosios" value={grams(extended.satFatG)} />
          <Cell label="Skaid." full="Skaidulos" value={grams(extended.fiberG)} />
          <Cell
            label="Druska"
            value={extended.saltG == null ? '—' : `${extended.saltG.toFixed(1)} g`}
          />
        </>
      )}
    </dl>
  )
}

function grams(value: number | null): string {
  return value == null ? '—' : `${Math.round(value)} g`
}

function Cell({
  label,
  full,
  dot,
  value,
}: {
  label: string
  /** Spelled-out name for screen readers when the visible label is shorthand. */
  full?: string
  dot?: string
  value: string
}) {
  return (
    <div>
      <dt className="flex items-center gap-1 text-xs text-mute">
        {dot != null && (
          <span
            aria-hidden="true"
            className="size-1.5 shrink-0 rounded-full"
            style={{ background: dot }}
          />
        )}
        {full != null ? (
          <>
            <span aria-hidden="true">{label}</span>
            <span className="sr-only">{full}</span>
          </>
        ) : (
          label
        )}
      </dt>
      <dd className="tnum text-base text-ink">{value}</dd>
    </div>
  )
}
